import { useState, useEffect, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';

import { useRedirectAfterLogin } from '@/components/hooks/useRedirectAfterLogin';
import { logoutUser } from '@/components/utils/logoutUser';
import { useProfiles } from '@/components/hooks/useProfiles';

export interface UsuarioPerfil {
  nombre: string;
  telefono: string;
  email: string;
}

export const useUsuario = () => {
  const { redirect } = useRedirectAfterLogin();
  const { hasPerfil } = useProfiles();

  const [usuario, setUsuario] = useState<UsuarioPerfil>({
    nombre: '',
    telefono: '',
    email: '',
  });

  const [loadingUser, setLoadingUser] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loadingOAuth, setLoadingOAuth] = useState(false);

  /* ============================================================
     LOGIN EMAIL / PASSWORD
  ============================================================ */
  const login = useCallback(
    async (
      email: string,
      password: string,
      context: 'login' | 'clientBooking' = 'login'
    ) => {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error || !data.user) {
        toast.error('Credenciales incorrectas o error de conexión.');
        return { ok: false };
      }

      const user = data.user;

      localStorage.setItem('id_usuario', user.id);
      localStorage.setItem('email', user.email || '');
      localStorage.removeItem('id_perfil');
      localStorage.removeItem('id_empresa');

      if (context === 'clientBooking') {
        const esCliente = await hasPerfil('cliente');
        if (!esCliente) {
          await logoutUser();
          toast.warning('Esta cuenta no es de cliente');
          return { ok: false };
        }
      }

      Sentry.setUser({ id: user.id, email: user.email ?? undefined });

      setIsAuthenticated(true);
      await fetchUsuario();
      await redirect(user.id, context);

      return { ok: true };
    },
    [redirect, hasPerfil]
  );

  /* ============================================================
     LOGIN GOOGLE (OAUTH)
     Funciona tanto en web (redirect normal del navegador) como en
     nativo/Capacitor (abre el navegador del sistema con Custom Tabs
     y vuelve a la app via deep link — ver appUrlOpen en App.tsx).
     El path "/auth/callback" debe coincidir EXACTAMENTE (misma ruta,
     mismo formato de barras) con la Redirect URL registrada en
     Supabase → Authentication → URL Configuration para cada entorno
     (p.ej. com.bookoll.app:/auth/callback en prod,
     com.bookoll.qa:/auth/callback en QA).
  ============================================================ */
  const loginWithGoogle = useCallback(async () => {
    setLoadingOAuth(true);

    const isNative = Capacitor.isNativePlatform();
    const appScheme = import.meta.env.VITE_APP_SCHEME;

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: isNative
          ? `${appScheme}:/auth/callback`
          : `${window.location.origin}/auth/callback`,
        // En nativo evitamos que Supabase intente redirigir dentro del
        // WebView de la app: Google bloquea OAuth desde WebViews
        // embebidos (error disallowed_useragent). Abrimos nosotros el
        // navegador del sistema con @capacitor/browser en su lugar.
        skipBrowserRedirect: isNative,
      },
    });

    if (error) {
      Sentry.captureException(error);
      setLoadingOAuth(false);
      toast.error('Error al iniciar sesión con Google.');
      return;
    }

    if (isNative && data?.url) {
      await Browser.open({ url: data.url });
    }
    // En web, Supabase ya redirige la pestaña automáticamente
    // (skipBrowserRedirect: false), no hace falta hacer nada más aquí.
  }, []);

  /* ============================================================
     RESET PASSWORD
  ============================================================ */
  const resetPassword = useCallback(async (email: string) => {
    if (!email.trim()) {
      toast.error('Introduce tu correo para restablecer la contraseña.');
      return { ok: false };
    }

    // En nativo el destino tiene que ser el deep link de la app, no el origin
    // del WebView (que es https://localhost): el enlace del correo debe
    // reabrir la app, donde está el code verifier de PKCE que necesita
    // exchangeCodeForSession en SetNewPassword.
    const isNative = Capacitor.isNativePlatform();
    const appScheme = import.meta.env.VITE_APP_SCHEME;

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: isNative
        ? `${appScheme}:/set-new-password`
        : `${window.location.origin}/set-new-password`,
    });

    if (error) {
      Sentry.captureException(error);
      toast.error('Error al enviar la recuperación.');
      return { ok: false };
    }

    toast.success(
      'Hemos enviado un correo con las instrucciones para restablecer tu contraseña.'
    );

    return { ok: true };
  }, []);

  /* ============================================================
     LOGOUT
  ============================================================ */
  const logout = useCallback(async () => {
    await logoutUser();
    Sentry.setUser(null);
    setUsuario({ nombre: '', telefono: '', email: '' });
    setIsAuthenticated(false);
    return { ok: true };
  }, []);

  /* ============================================================
     FETCH USUARIO
  ============================================================ */
  const fetchUsuario = useCallback(async () => {
    setLoadingUser(true);

    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();

    if (error || !user) {
      setIsAuthenticated(false);
      setLoadingUser(false);
      return;
    }

    Sentry.setUser({ id: user.id, email: user.email ?? undefined });

    setIsAuthenticated(true);

    // nombre_app es el nombre que el usuario escribe en Bookoll. full_name lo
    // pisa Google en cada inicio de sesión con su cuenta, así que no sirve
    // como fuente de verdad: solo como valor inicial.
    setUsuario({
      nombre: user.user_metadata?.nombre_app || user.user_metadata?.full_name || '',
      telefono: user.user_metadata?.telefono || '',
      email: user.email || '',
    });

    setLoadingUser(false);
  }, []);

  /* ============================================================
     GUARDAR PERFIL
  ============================================================ */
  const guardarUsuario = useCallback(
    async (nuevoUsuario: UsuarioPerfil) => {
      setSaving(true);

      const { error } = await supabase.auth.updateUser({
        data: {
          full_name: nuevoUsuario.nombre,
          nombre_app: nuevoUsuario.nombre,
          telefono: nuevoUsuario.telefono,
        },
      });

      setSaving(false);

      if (error) {
        Sentry.captureException(error);
        toast.error('No se pudieron guardar los cambios.');
        return { ok: false };
      }

      toast.success('Perfil actualizado.');
      fetchUsuario();
      return { ok: true };
    },
    [fetchUsuario]
  );

  /* ============================================================
     CARGA INICIAL
  ============================================================ */
  useEffect(() => {
    fetchUsuario();
  }, [fetchUsuario]);

  /* ============================================================
     RETURN
  ============================================================ */
  return {
    usuario,
    loadingUser,
    saving,
    isAuthenticated,
    loadingOAuth,

    login,
    loginWithGoogle,
    logout,
    resetPassword,
    fetchUsuario,
    guardarUsuario,
    setUsuario,
  };
};